import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import moment from 'moment';
import axios from 'axios';

import {
  Common, FormHelpers, ScheduleOfValues, TaskHelpers,
} from 'ontraccr-common';
import { message } from 'antd';
import { STANDARD_PDF_FONTS } from '../common/pdf/PDFDesigner/PDFDesigner.constants';
import { uploadFiles } from '../helpers/requests';
import { isNullOrUndefined } from '../helpers/helpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';
import { isEntryTimeValid } from './FormBuilder/FormFields/TimeEntryTable/TimeEntryTable.helpers';
import { getTrueFileType } from '../files/fileHelpers';
import { currencyFormatter, currencyParser } from '../helpers/inputParsers';
import {
  generateId,
  isHiddenSectionStaticTextField,
  syncHiddenSectionStaticTextResponses,
} from './formHelpers';

// FORM RESPONDER MODES:
export const DEFAULT_FORM_RESPONDER_MODE = 'Default';
export const PO_FORM_RESPONDER_MODE = 'PO';
export const SUB_CONTRACT_FORM_RESPONDER_MODE = 'Sub-Contract';

const attachmentDataTypes = new Set(['Materials', 'Equipment', 'Shifts']);

export async function uploadTableRowAttachments({
  ourValues,
  fileList,
  shouldUploadFiles,
  uploadFilesOptions = {},
}) {
  const toUpload = [];
  const uploadIndexByRowAtt = new Map();
  ourValues.forEach((row, rowIdx) => {
    const atts = Array.isArray(row.attachments) ? row.attachments : [];
    atts.forEach((att, attIdx) => {
      const isFile = att instanceof File || (att?.originFileObj);
      const hasId = att && typeof att.id === 'string';
      if (isFile && !hasId) {
        const file = att instanceof File ? att : att.originFileObj;
        uploadIndexByRowAtt.set(`${rowIdx}-${attIdx}`, toUpload.length);
        toUpload.push(file);
      }
    });
  });

  let uploadedByIdx = [];
  let newFileList = fileList;
  if (toUpload.length > 0 && shouldUploadFiles) {
    const uploaded = await uploadFiles({ files: toUpload, ...uploadFilesOptions });
    uploadedByIdx = uploaded.map((f) => ({ id: f.id, name: f.name }));
    newFileList = fileList.concat(uploaded.filter((f) => !f.isAutosaved));
  }

  const updatedValues = ourValues.map((row, rowIdx) => {
    if (!Array.isArray(row.attachments)) return row;
    const atts = row.attachments.map((att, attIdx) => {
      const uploadIdx = uploadIndexByRowAtt.get(`${rowIdx}-${attIdx}`);
      if (uploadIdx !== undefined && uploadedByIdx[uploadIdx]) return uploadedByIdx[uploadIdx];
      if (att?.id || att?.name) {
        return { id: att.id, name: att.name ?? (att.originFileObj?.name) };
      }
      return null;
    }).filter(Boolean);
    return { ...row, attachments: atts };
  });

  return { ourValues: updatedValues, fileList: newFileList };
}

// EDIT TYPES
export const EDIT_TYPES = {
  EDIT: 'edit',
  RESUBMIT: 'resubmission',
};

export const startsWithDollarSign = (value) => {
  if (value === null || value === undefined) return false;
  return value.toString().startsWith('$');
};

export const formatCurrencyCalculation = (value = '$ ') => {
  const numeric = Number.parseFloat(currencyParser(String(value ?? '')));
  const currencyValue = (Number.isNaN(numeric) ? 0 : numeric).toFixed(2);
  return `$ ${currencyValue}`;
};

export const formatTableValues = (values = [], columns = []) => {
  const currencyColumnKeys = columns.reduce((acc, col) => {
    if ((col.isCalculation || col.isCustomText) && col.isCurrency) {
      acc.push(col.key);
    }
    return acc;
  }, []);
  if (currencyColumnKeys.length === 0) return values;
  return values.map((row) => {
    const newRow = { ...row };
    currencyColumnKeys.forEach((key) => {
      const value = row[key];
      const formattedValue = startsWithDollarSign(value) ? value.replace(/\$\s?/g, '') : value;
      newRow[key] = currencyFormatter(formattedValue);
    });
    return newRow;
  });
};

const getRequiredCustomFields = (timeEntryCardSections = [], visibleFieldIds = null) => {
  const requiredFields = {};
  timeEntryCardSections.forEach((section) => {
    if (section.id === 'timecard') return; // Skip system fields
    const { fields = [] } = section;
    fields.forEach((field) => {
      const { id: fieldId, configProps = {} } = field;
      const { optional } = configProps;

      if (visibleFieldIds && !visibleFieldIds.has(fieldId)) return;

      if (optional !== true) {
        requiredFields[fieldId] = field;
      }
    });
  });
  return requiredFields;
};

const normalizeTimeEntryCustomData = (customData = {}) => {
  if (!Array.isArray(customData)) return { ...customData };

  const newCustomData = {};
  customData.forEach((section) => {
    section?.fields?.forEach((field) => {
      if (!field.fieldId) return;
      newCustomData[field.fieldId] = field.response;
    });
  });
  return newCustomData;
};

export const getResolvedCostcodeId = (costcodeId) => {
  if (!costcodeId || typeof costcodeId !== 'string') return costcodeId ?? null;
  if (!costcodeId.includes('.')) return costcodeId;
  const [, resolvedCostcodeId] = costcodeId.split('.');
  return resolvedCostcodeId ?? null;
};

/**
 * Build the response map used for time-entry custom field validation / conditionals.
 * Injects live project/costcode from the row so formulas tied to timecard system fields
 * evaluate correctly even when customData.project was never synced.
 */
const getTimeEntryRowResponses = (row = {}) => {
  const customData = normalizeTimeEntryCustomData(row.customData || {});
  const costcodeId = getResolvedCostcodeId(row.costcodeId);

  return {
    ...customData,
    project: {
      ...(customData.project ?? {}),
      value: row.projectId ?? customData.project?.value ?? null,
    },
    costcode: {
      ...(customData.costcode ?? {}),
      value: costcodeId ?? customData.costcode?.value ?? null,
    },
  };
};

const getTimeEntryCodingError = ({
  values = [],
  companySettings = {},
  timeEntryCardCustomFields = {},
  checkErrorForField: validateField,
}) => {
  const { reqProject = false, reqCostcode = false } = companySettings;

  const hasRequiredCustomFields = values.some((row) => {
    const rowDivisionId = row.divisionId;
    if (rowDivisionId && timeEntryCardCustomFields[rowDivisionId]) {
      const divisionSections = timeEntryCardCustomFields[rowDivisionId].fields || [];
      const requiredFields = getRequiredCustomFields(divisionSections);
      return Object.keys(requiredFields).length > 0;
    }
    return false;
  });

  const hasRequiredFields = reqProject || reqCostcode || hasRequiredCustomFields;

  if (!hasRequiredFields) return null;

  // Check each time entry for missing required fields
  const missingFields = [];
  const missingCustomFields = new Set();

  const hasMissing = values.some((row) => {
    let entryHasMissing = false;

    // Check project
    if (reqProject && !row.projectId) {
      if (!missingFields.includes('Project')) {
        missingFields.push('Project');
      }
      entryHasMissing = true;
    }

    // Check costcode
    if (reqCostcode && !row.costcodeId) {
      if (!missingFields.includes('Cost Code')) {
        missingFields.push('Cost Code');
      }
      entryHasMissing = true;
    }

    const rowDivisionId = row.divisionId;
    if (rowDivisionId && timeEntryCardCustomFields[rowDivisionId]) {
      const divisionSections = timeEntryCardCustomFields[rowDivisionId].fields || [];
      const responses = getTimeEntryRowResponses(row);
      const visibleFieldIds = FormHelpers.getVisibleFields({
        sections: divisionSections,
        responses,
      });
      const requiredCustomFields = getRequiredCustomFields(divisionSections, visibleFieldIds);

      Object.keys(requiredCustomFields).forEach((fieldId) => {
        const customField = requiredCustomFields[fieldId];

        const fieldErrorMap = validateField({
          field: customField,
          errorMap: {},
          responses,
          companySettings,
          timeEntryCardSections: divisionSections,
        });

        if (fieldErrorMap[fieldId]) {
          const { configProps: { title } = {} } = customField;
          if (title) {
            missingCustomFields.add(title);
            entryHasMissing = true;
          }
        }
      });
    }

    return entryHasMissing;
  });

  if (!hasMissing) return null;

  const allMissingFields = [...missingFields, ...Array.from(missingCustomFields)];

  if (allMissingFields.length === 0) return null;

  return `${allMissingFields.join(', ')} ${allMissingFields.length > 1 ? 'are' : 'is'} required for every time entry`;
};

const validateTimeEntryCurrentDate = ({ values = [], configProps = {} }) => {
  if (!configProps.preventNonCurrentDateSubmission) return null;

  const today = DateTime.local().startOf('day');
  const hasNonCurrentDate = values.some((entry) => {
    if (!entry.date) return false;

    const entryDate = DateTime.fromSQL(entry.date).startOf('day');
    if (entryDate.equals(today)) return false;

    if (entryDate.equals(today.minus({ days: 1 }))) {
      const timeFields = ['startTime', 'endTime', 'otStartTime', 'otEndTime', 'doubleOTStartTime', 'doubleOTEndTime', 'breakStartTime', 'breakEndTime', 'ptoStartTime', 'ptoEndTime'];

      const hasTimeFieldInToday = timeFields.some((field) => {
        if (entry[field]) {
          const timeDT = DateTime.fromMillis(entry[field]);
          return timeDT.startOf('day').equals(today);
        }
        return false;
      });

      if (hasTimeFieldInToday) {
        return false;
      }
    }

    return true;
  });

  return hasNonCurrentDate ? 'You can only submit time entries for the current day (today)' : null;
};

// Backwards compatible.
// Versions <= 2.3.2 store dropdown values as strings, instead of objects
export const getDropdownId = (selectedItem) => (
  typeof selectedItem === 'string'
    ? selectedItem
    : selectedItem.id
);

let VALID_CHARACTERS = new Set();
const preloadValidTextCharacters = async () => {
  const fakeDOC = await PDFDocument.create();
  // Only use standard PDF fonts for character validation (custom fonts aren't in StandardFonts)
  const fonts = await Promise.all(STANDARD_PDF_FONTS.map((fontName) => {
    const font = StandardFonts[fontName];
    return fakeDOC.embedFont(font, { subset: true });
  }));
  fonts.forEach((font) => {
    const fontChars = font.getCharacterSet();
    if (VALID_CHARACTERS.size > 0) {
      const charIntersection = new Set();
      fontChars.forEach((charCode) => {
        if (VALID_CHARACTERS.has(charCode)) {
          charIntersection.add(charCode);
        }
      });
      VALID_CHARACTERS = charIntersection;
    } else {
      // Add
      fontChars.forEach((charCode) => {
        VALID_CHARACTERS.add(charCode);
      });
    }
  });
};
preloadValidTextCharacters();

const findBadInCharacters = (text = '') => {
  if (!text || typeof text !== 'string') return [];
  const badChars = [];
  for (let i = 0; i < text.length; i += 1) {
    const charCode = text.charCodeAt(i);
    if (text[i] !== '\n' && !VALID_CHARACTERS.has(charCode)) {
      badChars.push(text[i]);
    }
  }
  return badChars;
};

const timeEntryKeysToIgnoreSet = new Set(['time', 'enteredVia']);

const getTableColumnDataKey = (column, dataType) => {
  if ((dataType === 'Materials' || dataType === 'Equipment') && column.key === 'vendor') return 'vendorId';
  if (dataType === 'Shifts') {
    if (column.key === 'users') return 'userIds';
    if (column.key === 'dates') return 'times';
  }
  if (dataType === 'Materials' && column.key === 'location') return 'locationId';
  return column.key;
};

// checks if response object has any empty values
const hasEmptyColumn = ({
  columns,
  dataType,
  values,
}) => {
  let columnKeys = columns.map((column) => getTableColumnDataKey(column, dataType));

  if (dataType === 'TimeEntry') {
    columnKeys = columnKeys.filter((key) => !timeEntryKeysToIgnoreSet.has(key) && !key.startsWith('field-'));
  }

  // A parent with sub-items shows rollups in its read-only columns, so only
  // its editable columns can be required.
  const sovParentIds = dataType === 'ScheduleOfValues'
    ? ScheduleOfValues.getAggregateParentIdSet(values)
    : null;
  const sovParentReadOnlyKeys = new Set(ScheduleOfValues.SOV_PARENT_READ_ONLY_FIELDS);

  const hasEmptyValue = values.some((value) => (
    columnKeys.some((key) => {
      if (sovParentIds?.has(ScheduleOfValues.getSovRowId(value)) && sovParentReadOnlyKeys.has(key)) {
        return false;
      }
      if (dataType === 'Shifts' && key === 'times') {
        return !value?.times?.length && (!value?.startTime || !value?.endTime);
      }
      return Common.isEmptyInObject(value, key);
    })
  ));

  return hasEmptyValue;
};

export const generateConfigPropsAndSettingsMap = (sections = []) => {
  const configPropMap = {};
  const settingsMap = {};

  sections?.forEach((section) => {
    const { fields = [], id, settings } = section;
    settingsMap[id] = { settings };
    fields.forEach((field) => {
      const { id: fieldId, configProps } = field;
      configPropMap[fieldId] = configProps;
    });
  });

  return {
    configPropMap,
    settingsMap,
  };
};

export const generateResponseMap = (sections = []) => sections?.reduce((acc, section) => {
  const { fields = [] } = section;
  fields.forEach((field) => {
    const { fieldId, response } = field;
    acc[fieldId] = response;
  });
  return acc;
}, {});

const validateTimeTrackingGrid = ({
  id,
  responses,
  errorMap,
  isProjectRequired,
  isCostcodeRequired,
}) => {
  const newErrorMap = { ...errorMap };
  const {
    [id]: {
      columns = [],
    } = {},
  } = responses;

  if (isProjectRequired && columns.some((value) => !value.project?.id)) {
    newErrorMap[id] = 'Project is required for every column';
  }

  if (isCostcodeRequired && columns.some((value) => !value.costcode?.id)) {
    newErrorMap[id] = 'Cost code is required for every column';
  }

  return newErrorMap;
};

const checkErrorForField = ({
  field = {},
  errorMap = {},
  responses = {},
  companySettings = {},
  timeEntryCardSections = [],
  timeEntryCardCustomFields = {},
  isApproval = false,
}) => {
  let newErrorMap = { ...errorMap };
  const {
    configProps = {},
    id,
    selectedType,
  } = field;
  if (selectedType === 'dynamicAttribute') {
    const fieldType = configProps.fieldType || 'text';
    return checkErrorForField({
      field: { ...field, selectedType: fieldType },
      errorMap: newErrorMap,
      responses,
      companySettings,
      timeEntryCardSections,
      timeEntryCardCustomFields,
      isApproval,
    });
  }
  const {
    optional,
    hasConditionalRendering,
    conditionalRenderingFormula,
    isProjectRequired,
    isCostcodeRequired,
  } = configProps;
  if (selectedType === 'text') {
    const {
      [id]: { value } = {},
    } = responses;
    const badCharacters = findBadInCharacters(value);
    if (badCharacters.length > 0) {
      newErrorMap[id] = `Found invalid characters: ${badCharacters.join(' ')}`;
      return newErrorMap;
    }
  }

  if (
    hasConditionalRendering
    && FormHelpers.isConditionalRenderingFormulaComplete(conditionalRenderingFormula)
    && !FormHelpers.executeConditionalRenderingFormula({
      formula: conditionalRenderingFormula,
      responses,
    })
  ) {
    return newErrorMap;
  }

  if (optional) {
    switch (selectedType) {
      case 'dateRange': {
        const {
          [id]: {
            startTime,
            endTime,
            repeat,
            repeatEndDate,
          } = {},
        } = responses;
        if ((!startTime && endTime) || (startTime && !endTime) || (repeat && !repeatEndDate)) {
          newErrorMap[id] = 'Please select a start and end time';
        }
        break;
      }
      case 'table': {
        // empty response for optional allowed
        if (!responses[id] || !responses[id].values || responses[id].values.length === 0) {
          break;
        }

        const {
          requiredColumns,
          dataType,
          columns = [],
        } = configProps;

        const {
          [id]: {
            values = [],
          } = {},
        } = responses;

        // if there is an entry and required columns, all columns must be filled
        if (requiredColumns) {
          if (hasEmptyColumn({ columns, dataType, values })) newErrorMap[id] = 'Please fill in all columns';
          break;
        }

        if (dataType === 'TimeEntry' && values.some((val) => Common.isEmptyInObject(val, 'date'))) {
          newErrorMap[id] = 'Please enter a valid date for time entries';
        }

        if (dataType === 'TimeEntry') {
          const err = getTimeEntryCodingError({
            values,
            companySettings,
            timeEntryCardCustomFields,
            checkErrorForField,
          });
          if (err) newErrorMap[id] = err;
        }

        if (dataType === 'TimeEntry' && !isApproval) {
          const currentDateError = validateTimeEntryCurrentDate({ values, configProps });
          if (currentDateError) newErrorMap[id] = currentDateError;
        }

        break;
      }
      case 'timeTrackingGrid': {
        newErrorMap = validateTimeTrackingGrid({
          id,
          responses,
          errorMap: newErrorMap,
          isProjectRequired,
          isCostcodeRequired,
        });
        break;
      }
      default:
        break;
    }
    return newErrorMap;
  }
  switch (selectedType) {
    case 'yes-no': {
      const {
        explain = null,
      } = configProps;
      const parsedValue = TaskHelpers.parseYesNoResponse(responses[id] || {});
      if (!parsedValue) {
        newErrorMap[id] = 'Please select an option';
      } else if (explain && explain.includes(parsedValue) && !responses[id].explanation) {
        newErrorMap[id] = 'Please explain your selection';
      }
      break;
    }
    case 'gpsLocation':
    case 'dropdown': {
      const {
        [id]: {
          values = [],
        } = {},
      } = responses;
      if (configProps.openLimit) {
        if (!values || values.length === 0) {
          newErrorMap[id] = 'Please select an option';
        }
        break; // Allow any number of answers
      }

      // If not open limit, check for required number of answers
      const requiredNumAnswers = configProps.numAnswers ?? 1;

      if (!values || (values && values.length < requiredNumAnswers)) {
        newErrorMap[id] = `Please choose ${requiredNumAnswers} options`;
      }
      break;
    }
    case 'attribute':
    case 'text': {
      const { allowEdits = true, isNumerical } = configProps;
      const shouldEdit = allowEdits || selectedType !== 'attribute';
      const fieldResponse = responses[id];
      const val = fieldResponse?.value;
      const isEmpty = !fieldResponse || isNullOrUndefined(val) || (typeof val === 'string' && val.trim() === '');

      if (isEmpty && shouldEdit) {
        newErrorMap[id] = isNumerical
          ? 'Please enter a number'
          : 'Please enter your response';
      }
      break;
    }
    case 'multiSig': {
      const {
        [id]: {
          values = [],
        } = {},
      } = responses;
      if (values.length === 0) {
        newErrorMap[id] = 'Please add at least one signature';
      } else {
        const missingSigs = values.filter((val) => !val.sig);
        if (missingSigs.length > 0) {
          const names = missingSigs.map((val) => val.name).join(', ');
          newErrorMap[id] = `Missing signatures for: ${names}`;
        }
      }
      break;
    }
    case 'attachment': {
      const files = responses[id] ? (responses[id].files || responses[id].fileIds) : [];
      if (!files || (files && !files.length)) {
        newErrorMap[id] = 'Please attach the corresponding file(s)';
      }
      break;
    }
    case 'table': {
      const {
        requiredColumns,
        dataType,
        columns = [],
      } = configProps;

      if (!responses[id] || !responses[id].values || responses[id].values.length === 0) {
        newErrorMap[id] = 'Please enter an entry';
      }

      const {
        [id]: {
          values = [],
        } = {},
      } = responses;

      if (requiredColumns) {
        if (hasEmptyColumn({ columns, dataType, values })) newErrorMap[id] = 'Please fill in all columns';
        break;
      }

      if (dataType === 'TimeEntry' && values.some((value) => Common.isEmptyInObject(value, 'date'))) {
        newErrorMap[id] = 'Please enter a valid date for time entries';
      }

      if (dataType === 'TimeEntry') {
        const err = getTimeEntryCodingError({
          values,
          companySettings,
          timeEntryCardCustomFields,
          checkErrorForField,
        });
        if (err) newErrorMap[id] = err;
      }

      if (dataType === 'TimeEntry' && !isApproval) {
        const currentDateError = validateTimeEntryCurrentDate({ values, configProps });
        if (currentDateError) newErrorMap[id] = currentDateError;
      }

      break;
    }
    case 'dateRange': {
      const {
        [id]: {
          startTime,
          endTime,
          repeat,
          repeatEndDate,
        } = {},
      } = responses;
      if (!(startTime && endTime)) {
        newErrorMap[id] = 'Please select a start and end time';
      } else if (repeat && !repeatEndDate) {
        newErrorMap[id] = 'Please select a repeat end date';
      }
      break;
    }
    case 'dateTime': {
      const {
        hideDate = false,
        hideTime = false,
      } = configProps;
      const {
        [id]: {
          date,
          time,
        } = {},
      } = responses;
      const needsBoth = !hideDate && !hideTime;
      const needsDate = !hideDate;
      const needsTime = !hideTime;
      if (needsBoth) {
        if (!date || !time) {
          newErrorMap[id] = 'Please select a date and time';
        }
      } else if (needsDate && !date) {
        newErrorMap[id] = 'Please select a date';
      } else if (needsTime && !time) {
        newErrorMap[id] = 'Please select a time';
      }
      break;
    }
    case 'weather': {
      const {
        [id]: {
          value,
        } = {},
      } = responses;
      if (!value) {
        newErrorMap[id] = 'Please set a weather location';
      }
      break;
    }
    case 'timeTrackingGrid': {
      const {
        [id]: {
          values = [],
          columns = [],
          date,
        } = {},
      } = responses;

      if (!values?.length) {
        newErrorMap[id] = 'Please enter a user or equipment';
      } else if (!columns?.length) {
        newErrorMap[id] = 'Please enter a cost code';
      } else if (!date) {
        newErrorMap[id] = 'Please select a date';
      }

      newErrorMap = validateTimeTrackingGrid({
        id,
        responses,
        errorMap: newErrorMap,
        isProjectRequired,
        isCostcodeRequired,
      });
      break;
    }
    default:
      break;
  }
  return newErrorMap;
};

export default {};

export const getErrorsFromResponses = ({
  sections,
  responses,
  sectionPermissionMap,
  timecardPermissionMap,
  companySettings = {},
  timeEntryCardSections = [],
  timeEntryCardCustomFields = {},
  isApproval = false,
}) => {
  let errorMap = {};
  let wentThroughFields = false;
  if (!sections || !sections.length) return { errorMap, wentThroughFields: true };
  sections.forEach(({
    id,
    fields,
    hidden,
    settings: {
      hasConditionalRendering,
      conditionalRenderingFormula,
    } = {},
  }, index) => {
    const hasSectionPermission = !sectionPermissionMap
      || (sectionPermissionMap[id]?.canView && sectionPermissionMap[id]?.canEdit);

    const hasTimecardPermission = !timecardPermissionMap
      || (timecardPermissionMap[id]?.canView && timecardPermissionMap[id]?.canEdit);

    const hasPermission = hasSectionPermission && hasTimecardPermission;
    const isConditionallyVisible = (
      !hasConditionalRendering
        || !FormHelpers.isConditionalRenderingFormulaComplete(conditionalRenderingFormula)
        || FormHelpers.executeConditionalRenderingFormula({
          formula: conditionalRenderingFormula,
          responses,
        })
    );

    if (!hidden && hasPermission && isConditionallyVisible) {
      const safeFields = fields ?? [];
      safeFields.forEach((field) => {
        errorMap = checkErrorForField({
          field,
          errorMap,
          responses,
          companySettings,
          timeEntryCardSections,
          timeEntryCardCustomFields,
          isApproval,
        });
      });
    }
    if (index === sections.length - 1) {
      wentThroughFields = true;
    }
  });
  return { errorMap, wentThroughFields };
};

export const prepareResponsePayload = ({
  sections = [],
  responses = {},
  title,
  templateId,
  assignedFormId,
  assignedDraftId,
  projectId,
  projectIds = [],
  bucketIds = [],
  customerId,
  customerIds = [],
  vendorId,
  cardId,
  equipmentId,
  valueFieldId,
  fieldsToInclude,
  hiddenFields,
  collectedData,
}) => {
  const projectIdSet = new Set();
  const bucketIdSet = new Set();
  const customerIdSet = new Set();
  if (projectId) projectIdSet.add(projectId);
  if (projectIds) projectIds.forEach((id) => id && projectIdSet.add(id));
  if (bucketIds) bucketIds.forEach((id) => id && bucketIdSet.add(id));
  if (customerId) customerIdSet.add(customerId);
  if (customerIds) customerIds.forEach((id) => id && customerIdSet.add(id));

  const vendorIdSet = new Set();
  if (vendorId) vendorIdSet.add(vendorId);

  const collected = {
    ...(collectedData || {}),
  };
  const payloadResponse = {};
  sections.forEach((section) => {
    const {
      fields = [],
      duplicatedParentId: sectionDuplicatedParentId,
      id: sectionId,
      name: sectionName,
    } = section;
    fields.forEach((field) => {
      const {
        configProps = {},
        selectedType,
        id: fieldId,
        duplicatedParentId,
      } = field;
      const {
        [fieldId]: response = {},
      } = responses;
      if (fieldsToInclude && !fieldsToInclude.has(fieldId)) return;
      if (selectedType === 'dropdown') {
        const { dataType, shouldDisableSmartFiltering = false } = configProps;
        const { values = [] } = response;
        if (!shouldDisableSmartFiltering) {
          if (dataType === 'Customers') {
            values.forEach(({ id }) => customerIdSet.add(id));
          } else if (dataType === 'Projects') {
            values.forEach(({ id }) => projectIdSet.add(id));
          } else if (dataType === 'Vendors') {
            values.forEach(({ id }) => vendorIdSet.add(id));
          } else if (dataType === 'Buckets') {
            values.forEach(({ id }) => bucketIdSet.add(id));
          }
        }
      }

      let formattedResponse = { ...response };

      if (selectedType === 'staticAttachments') {
        formattedResponse.files = response.files ?? configProps?.files?.map(({ id }) => id) ?? [];
      }

      const dynamicType = response.type ?? configProps?.fieldType ?? selectedType;
      payloadResponse[fieldId] = {
        ...(configProps),
        ...formattedResponse,
        fieldId,
        type: selectedType === 'dynamicAttribute' ? dynamicType : selectedType,
        duplicatedParentId,
        sectionDuplicatedParentId,
        sectionId,
        sectionName,
      };
    });
  });

  collected.projectIds = [...projectIdSet];
  collected.vendorIds = [...vendorIdSet];
  collected.bucketIds = [...bucketIdSet];
  collected.customerIds = [...customerIdSet];
  return {
    collected,
    responses: payloadResponse,
    name: title,
    templateId,
    id: assignedFormId,
    draftId: assignedDraftId,
    cardId,
    equipmentId,
    valueFieldId,
    hiddenFields,
  };
};

const getShallowFormFileFromMap = (fileId, fileMap = {}) => {
  const file = { ...fileMap[fileId] };
  const type = file.trueType ?? getTrueFileType(file);
  return {
    ...file,
    existing: true,
    type,
  };
};

export const hasHiddenSectionStaticTextToPreload = ({
  sections = [],
  sectionPermissionMap,
}) => {
  if (!sectionPermissionMap) return false;
  return sections.some((section) => {
    const { fields = [] } = section;
    return fields.some((field) => isHiddenSectionStaticTextField(section, field, sectionPermissionMap));
  });
};

export const preparePreloadedFormTriggerData = ({
  initialData = {},
  fileMap = {},
  sections: templateSections = [],
  templateProjectId,
  templateCustomerId,
  parentFormId,
  parentTemplateId,
  projectIdMap = {},
  customerIdMap = {},
  formTemplateMap = {},
  initialCardId,
  initialCardTitle,
  initialCardBoardId,
  initialEquipmentId,
  initialEquipmentTitle,
  equipmentIdMap = {},
  sectionPermissionMap,
}) => {
  const configMap = {};
  const newResponses = {};
  const hasInitialCard = initialCardId && initialCardTitle;
  const hasInitialEquipment = initialEquipmentId && initialEquipmentTitle;
  templateSections.forEach(({ fields: templateFields = [] }) => {
    templateFields.forEach((templateField) => {
      const { configProps = {}, id: templateFieldId, selectedType } = templateField;
      const {
        dataType,
        subDataType,
      } = configProps;
      configMap[templateFieldId] = configProps;

      // Handle dropdown fields
      if (
        (
          !templateProjectId
            && !parentFormId
            && !hasInitialCard
            && !hasInitialEquipment
        )
        || selectedType !== 'dropdown'
      ) {
        // Check if this is a table field that needs equipment preloading
        if (selectedType === 'table' && dataType === 'Equipment' && hasInitialEquipment) {
          const equipment = equipmentIdMap[initialEquipmentId];
          if (equipment) {
            const {
              id,
              name,
              code,
              currentHours,
              hourlyCost,
              dailyCost,
              weeklyCost,
              monthlyCost,
              hourlyBillingRate,
              dailyBillingRate,
              weeklyBillingRate,
              monthlyBillingRate,
              currentProjectId,
              costcodeId,
            } = equipment;

            // Get current project name if available
            const currentProject = projectIdMap[currentProjectId];
            const currentProjectName = currentProject?.name || '';

            // Create a table row with the equipment data
            newResponses[templateFieldId] = {
              values: [{
                id: generateId(),
                equipmentId: id,
                name,
                code,
                currentHours,
                hourlyCost,
                dailyCost,
                weeklyCost,
                monthlyCost,
                hourlyBillingRate,
                dailyBillingRate,
                weeklyBillingRate,
                monthlyBillingRate,
                currentProjectName,
                costcodeId,
                addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
                updatedDate: DateTime.local().toISO(),
              }],
            };
          }
        }
        return;
      }
      if (
        dataType !== 'Projects'
        && dataType !== 'Customers'
        && dataType !== 'CompletedForms'
        && dataType !== 'Cards'
        && dataType !== 'Equipment'
      ) return;
      if (dataType === 'Projects' && templateProjectId) {
        const {
          [templateProjectId]: { name: projectName = '' } = {},
        } = projectIdMap;
        newResponses[templateFieldId] = {
          values: [{ id: templateProjectId, name: projectName }],
        };
      } else if (dataType === 'Customers' && templateCustomerId) {
        const {
          [templateCustomerId]: { name: customerName = '' } = {},
        } = customerIdMap;
        newResponses[templateFieldId] = {
          values: [{ id: templateCustomerId, name: customerName }],
        };
      } else if (dataType === 'CompletedForms' && parentFormId) {
        const {
          [parentTemplateId]: { name: templateName = '' } = {},
        } = formTemplateMap;
        newResponses[templateFieldId] = {
          values: [{ id: parentFormId, name: templateName }],
        };
      } else if (
        dataType === 'Cards'
        && hasInitialCard
        && subDataType === initialCardBoardId
      ) {
        newResponses[templateFieldId] = {
          values: [{ id: initialCardId, name: initialCardTitle }],
        };
      } else if (dataType === 'Equipment' && hasInitialEquipment) {
        newResponses[templateFieldId] = {
          values: [{ id: initialEquipmentId, name: initialEquipmentTitle }],
        };
      }
    });
  });

  const {
    sections: initialSections = [],
  } = initialData || {};
  initialSections.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      const { fieldId, response, type } = field;
      if (type === 'attachment') {
        const { fileIds = [], timestamps } = response;
        newResponses[fieldId] = {
          files: fileIds.map((fileId) => getShallowFormFileFromMap(fileId, fileMap)),
          timestamps,
        };
      } else if (type === 'staticAttachments') {
        const { fileIds } = response;
        newResponses[fieldId] = {
          files: fileIds,
        };
      } else if (type === 'multiSig') {
        const { values = [] } = response;
        newResponses[fieldId] = {
          values: values.map((obj) => {
            const { sig } = obj;
            const res = { ...obj };
            if (sig) res.sig = getShallowFormFileFromMap(sig, fileMap);
            return res;
          }),
        };
      } else if (type === 'table') {
        const {
          [fieldId]: {
            dataType: tableDataType,
          } = {},
        } = configMap;
        // Prevent overwriting equipment table pre-fill when triggered form has no data
        if (
          tableDataType === 'Equipment'
          && (!response?.values || response?.values?.length === 0)
        ) {
          // Keep the pre-filled equipment data from template
          const templateValues = newResponses[fieldId]?.values ?? [];
          newResponses[fieldId] = {
            ...response,
            values: templateValues,
            timezone: response.timezone,
          };
        } else {
          newResponses[fieldId] = {
            values: response.values,
            timezone: response.timezone,
          };
        }
      } else if (type === 'yes-no') {
        const {
          [fieldId]: {
            explain = [],
          } = {},
        } = configMap;
        newResponses[fieldId] = {
          value: TaskHelpers.parseYesNoResponse(response ?? {}),
        };
        if (explain.includes(newResponses[fieldId].value)) {
          newResponses[fieldId].explanation = response.explanation;
        }
      } else if (type === 'text') {
        if (response && response.value && typeof response.value === 'string') {
          const strippedValue = response.value.replace(/\r/g, '');
          newResponses[fieldId] = { ...response, value: strippedValue };
        } else {
          newResponses[fieldId] = response;
        }
      } else if (type === 'dropdown') {
        const {
          [fieldId]: {
            dataType,
          } = {},
        } = configMap;
        if (dataType === 'Costcodes' && response && response.values && Array.isArray(response.values)) {
          const newCostcodeResponseValues = response.values.map((value) => {
            const { id: costcodeId, name, phaseId } = value;
            return {
              name,
              id: phaseId === 'unphased' ? `unphased.${costcodeId}` : `${phaseId}.${costcodeId}`,
            };
          });
          newResponses[fieldId] = {
            ...response,
            values: newCostcodeResponseValues,
          };
        } else if (
          (dataType === 'Cards' || dataType === 'Equipment')
          && (!response?.values || response?.values?.length === 0)
        ) {
          // prevent triggered forms with no initial data to overwrite card/equipment pre-fill
          const templateValues = newResponses[fieldId]?.values ?? [];
          newResponses[fieldId] = {
            ...response,
            values: templateValues,
          };
        } else {
          newResponses[fieldId] = response;
        }
      } else if (type === 'staticText') {
        if (!newResponses[fieldId]) {
          newResponses[fieldId] = response;
        }
      } else {
        newResponses[fieldId] = response;
      }
    });
  });

  const { responses: syncedResponses } = syncHiddenSectionStaticTextResponses({
    responses: newResponses,
    sections: templateSections,
    sectionPermissionMap,
  });

  return syncedResponses;
};

export const parseFormResponseToReadableFormat = ({
  templateSections = [],
  responseSections = [],
  projectIdMap = {},
  settings = {},
  enableTable = false,
  id,
}) => {
  const configMap = {};
  const newResponses = {
    id,
  };
  templateSections.forEach(({ fields: templateFields = [] }) => {
    templateFields.forEach((templateField) => {
      const { configProps = {}, id: templateFieldId } = templateField;
      configMap[templateFieldId] = configProps;
      newResponses[templateFieldId] = '-';
    });
  });

  responseSections.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      const { fieldId, response, type } = field;

      switch (type) {
        case 'yes-no': {
          const {
            [fieldId]: {
              explain = [],
            } = {},
          } = configMap;

          newResponses[fieldId] = TaskHelpers.parseYesNoResponse(response ?? {});

          if (explain.includes(newResponses[fieldId])) {
            newResponses[fieldId] += ` - ${response.explanation}`;
          }
          break;
        }
        case 'text':
        case 'calculation':
        case 'attribute': {
          if (response?.value && typeof response.value === 'string') {
            const strippedValue = response.value.replace(/\r/g, '');
            newResponses[fieldId] = strippedValue;
          } else {
            newResponses[fieldId] = response?.value;
          }
          break;
        }
        case 'dateTime': {
          let value = '';
          const {
            timezone = DateTime.local().zoneName,
          } = response ?? {};

          if (response?.date) {
            value = `${DateTime.fromMillis(response.date, { zone: timezone }).toLocaleString(DateTime.DATE_MED)} `;
          }

          if (response?.time) {
            value += DateTime
              .fromMillis(response.time, { zone: timezone })
              .toLocaleString(DateTime.TIME_SIMPLE);
          }

          newResponses[fieldId] = value;
          break;
        }
        case 'dateRange': {
          const {
            timezone = DateTime.local().zoneName,
          } = response ?? {};

          const value = `${
            response?.startTime
              ? DateTime
                .fromMillis(response.startTime, { zone: timezone })
                .toLocaleString(DateTime.DATETIME_MED)
              : ''
          } - ${
            response?.endTime
              ? DateTime
                .fromMillis(response.endTime, { zone: timezone })
                .toLocaleString(DateTime.DATETIME_MED)
              : ''
          }`;

          newResponses[fieldId] = value;
          break;
        }
        case 'dropdown': {
          const { dataType } = configMap[fieldId] ?? {};
          if (response?.values && Array.isArray(response.values)) {
            newResponses[fieldId] = response.values.map((value) => {
              const { name, id } = value;
              if (dataType === 'Projects') {
                const { number } = projectIdMap[id] ?? {};
                return formatProjectLabelFromCompanySettings({ name, number, settings });
              }
              return name;
            }).join(', ');
          }
          break;
        }
        case 'table': {
          if (enableTable) {
            const formattedResponses = TaskHelpers.parseTableResponse(response, response.dataType);
            Object.keys(formattedResponses).forEach((colKey) => {
              const values = formattedResponses[colKey];
              newResponses[`${fieldId}-${colKey}`] = values.join(', ');
            });
          } else {
            newResponses[fieldId] = response;
          }
          break;
        }
        default:
          newResponses[fieldId] = response;
          break;
      }
    });
  });
  return newResponses;
};

// Edit this as needed
const MAX_FILE_GB = 5;

// Do not edit the following lines
const MAX_FILE_UNIT = 1024 * 1024 * 1024; // 1 GB
const MAX_FILE_SIZE = MAX_FILE_GB * MAX_FILE_UNIT;

export const constructFormPayloadForAPI = async ({
  form: {
    id,
    collected,
    responses,
    name,
    templateId,
    isOnEditStep,
    isResubmit,
    isEdit,
    cardId,
    valueFieldId,
    isDraft,
    draftId,
    companyId,
  },
  shouldUploadFiles = true,
  addSectionId = false,
  isExternalForm = false,
}) => {
  const sectionMap = {};
  let fileList = [];

  /*
    This relies on insertion order being correct
    to preserve ordering:
    https://stackoverflow.com/questions/5525795/does-javascript-guarantee-object-property-order

    We should refactor this to match the template properly.
  */
  const parsedFields = await Promise.all(Object.values(responses).map(async (field) => {
    const {
      type,
      title,
      dataType,
      fieldId,
      sectionId,
      duplicatedParentId,
    } = field;
    const response = {};

    switch (type) {
      case 'yes-no': {
        let fieldValue = field?.value;
        if (typeof fieldValue === 'boolean') {
          fieldValue = fieldValue ? 'yes' : 'no';
        }
        fieldValue = fieldValue?.toLowerCase();
        if (fieldValue === 'yes') {
          response.value = true;
        } else if (fieldValue === 'no') {
          response.value = false;
        } else if (fieldValue === 'n/a') {
          response.value = null;
        } else {
          response.value = undefined;
        }
        if (field.explanation) {
          response.explanation = field.explanation;
        }
        break;
      }
      case 'dropdown': {
        if (dataType !== 'Costcodes') {
          response.values = field.values;
          break;
        }
        if (field.values && Array.isArray(field.values)) {
          response.values = field.values.map(({ id: compoundKey, name }) => {
            const [phaseId, costcodeId] = compoundKey.split('.');
            return {
              id: costcodeId,
              phaseId,
              name,
            };
          });
        } else {
          response.values = [];
        }
        break;
      }
      case 'calculation':
      case 'attribute':
      case 'text': {
        const {
          value = '',
          isCurrency,
        } = field;
        const isCalc = type === 'calculation';
        const prefix = isCalc && isCurrency && !startsWithDollarSign(value) ? '$ ' : '';
        response.value = `${prefix}${value}`;
        if (isCalc && isCurrency) response.value = formatCurrencyCalculation(response.value);
        break;
      }
      case 'staticText': {
        const {
          value = '',
        } = field;
        response.value = value;
        break;
      }
      case 'multiSig': {
        const { values: sigs = [] } = field;
        const numSigs = sigs.length;
        if (numSigs > 0) {
          const fileIds = new Array(numSigs);
          const toUpload = [];
          const uploadedIndices = [];
          const filePrefix = `${id ?? DateTime.local().toMillis()}-${sectionId}-${fieldId}-`;
          const rawFiles = sigs
            .map(({ sig, id: sigUserId, autoSaveId }) => {
              if (!sig) return null;
              const { existing, id: fileId } = sig;
              const file = new File([sig], `${filePrefix}${sigUserId}.png`, { type: 'image/png' });
              if (fileId) file.id = fileId;
              if (existing) file.existing = existing;
              file.autoSaveId = autoSaveId;
              if (!shouldUploadFiles) {
                file.id = sigUserId;
              }
              return file;
            });
          rawFiles.forEach((file, idx) => {
            if (!file) return;
            fileIds[idx] = file.id;
            if (!file?.existing) {
              toUpload.push(file);
              uploadedIndices.push(idx);
            }
          });
          let files = [];
          if (shouldUploadFiles) {
            if (toUpload.length > 0) {
              files = await uploadFiles({
                files: toUpload,
                isDraft,
                formId: id,
                draftId,
              });
              files.forEach((file, idx) => {
                if (idx >= uploadedIndices.length) {
                  // Something bad happened
                  Sentry.withScope(() => {
                    Sentry.captureException(new Error('Error uploading files'), { sigs });
                  });
                  return;
                }
                const fileIdx = uploadedIndices[idx];
                if (fileIdx >= numSigs) {
                  // Something bad happened
                  Sentry.withScope(() => {
                    Sentry.captureException(new Error('Error uploading files'), { sigs });
                  });
                  return;
                }
                fileIds[fileIdx] = file.id || file.uid;
              });
            }
          } else {
            files = rawFiles;
          }
          response.values = sigs.map((obj, idx) => {
            const { sig } = obj;
            const res = { ...obj };
            if (sig) {
              const ourFileId = fileIds[idx];
              res.sig = ourFileId;
            }
            return res;
          });
          const filesWithoutAutosaved = files.filter((file) => file && !file?.isAutosaved);
          fileList = fileList.concat(filesWithoutAutosaved);
        } else {
          response.values = [];
        }
        break;
      }
      case 'attachment': {
        const { files: fieldFiles = [], timestamps } = field;
        const numFiles = fieldFiles.length;

        // Files uploaded to ant upload module have a uid.
        // Used in controlled mode for preloaded schedule "events"
        if (numFiles > 0) {
          const fileIds = new Array(numFiles);
          const toUpload = [];
          const uploadedIndices = [];

          if (isExternalForm) {
            const totalFileSize = fieldFiles.reduce((acc, file) => acc + file.size, 0);

            if (totalFileSize > MAX_FILE_SIZE) {
              const msg = `File size limit exceeded. Maximum file size is ${MAX_FILE_GB} GB`;
              message.error(msg);
              throw new Error(msg);
            }
          }

          fieldFiles.forEach((file, idx) => {
            if (file.existing) {
              fileIds[idx] = file.id || file.uid;
            } else {
              toUpload.push(file);
              uploadedIndices.push(idx);
            }
          });
          let files = [];
          if (shouldUploadFiles) {
            if (toUpload.length > 0) {
              files = await uploadFiles({
                files: toUpload,
                isDraft,
                formId: id,
                draftId,
                companyId,
                isExternalForm,
              });
            }
          } else {
            files = field.files;
          }
          files.forEach((file, idx) => {
            if (idx >= uploadedIndices.length) {
              // Something bad happened
              Sentry.withScope(() => {
                Sentry.captureException(new Error('Error uploading files'), { fieldFiles });
              });
              return;
            }
            const fileIdx = uploadedIndices[idx];
            if (fileIdx >= numFiles) {
              // Something bad happened
              Sentry.withScope(() => {
                Sentry.captureException(new Error('Error uploading files'), { fieldFiles });
              });
              return;
            }
            fileIds[fileIdx] = file.id || file.uid;
          });
          files = files.map((file, idx) => (
            { ...file, rotation: toUpload[idx]?.rotation }
          ));
          response.fileIds = fileIds;
          response.timestamps = timestamps;
          // filter here because we need the fileIds for the response
          const filesWithoutAutosaved = files.filter((file) => !file.isAutosaved);
          fileList = fileList.concat(filesWithoutAutosaved);
        } else {
          response.fileIds = [];
        }
        break;
      }
      case 'table': {
        const {
          values = [],
          createTimeEntry,
        } = field;

        let ourValues = field.dataType === 'Materials'
          ? values.map((val) => {
            const newVal = { ...val };
            delete newVal.locations;
            return newVal;
          })
          : values;

        // recursively call the function for time entry table custom fields
        if (createTimeEntry && dataType === 'TimeEntry') {
          const newTasks = [];
          let fullFiles = [];
          await Promise.all(
            values.map(async (task = {}) => {
              const {
                customData,
              } = task;

              const newTask = {
                ...task,
                ...TaskHelpers.shiftTimesOnWrongDay(task),
              };

              if (customData && !Array.isArray(customData)) {
                const {
                  data: {
                    sections,
                  } = {},
                  files,
                } = await constructFormPayloadForAPI({
                  form: {
                    collected: {}, responses: customData,
                  },
                  addSectionId: true,
                });

                newTask.customData = sections;
                fullFiles = fullFiles.concat(files);
              }

              newTasks.push(newTask);
            }),
          );
          fileList = fileList.concat(fullFiles);
          ourValues = newTasks;
        }

        if (
          attachmentDataTypes.has(field.dataType)
          && ourValues.some(
            (row) => (
              Array.isArray(row?.attachments)
              && row.attachments.length > 0
            )
          )
        ) {
          const { ourValues: valuesAfterAttachments, fileList: fileListAfterAttachments } = await uploadTableRowAttachments({
            ourValues,
            fileList,
            shouldUploadFiles,
            uploadFilesOptions: {
              isDraft,
              formId: id,
              draftId,
              companyId,
              isExternalForm,
            },
          });
          ourValues = valuesAfterAttachments;
          fileList = fileListAfterAttachments;
        }

        response.values = formatTableValues(ourValues, field.columns);
        response.columns = field.columns;
        response.dataType = field.dataType;
        response.timezone = field.timezone;
        response.preloadSourceFormIds = field.preloadSourceFormIds;
        break;
      }
      case 'dateRange': {
        response.startTime = field.startTime;
        response.endTime = field.endTime;
        response.repeat = field.repeat;
        response.timezone = field.timezone;
        if (field.repeatEndDate) {
          if (moment.isMoment(field.repeatEndDate)) {
            response.repeatEndDate = field.repeatEndDate.valueOf();
          } else {
            response.repeatEndDate = field.repeatEndDate;
          }
        }
        break;
      }
      case 'dateTime': {
        response.date = field.date;
        response.time = field.time;
        response.timezone = field.timezone;
        break;
      }
      case 'payment': {
        response.paymentId = field.paymentId;
        response.succeeded = field.succeeded;
        break;
      }
      case 'staticAttachments': {
        response.fileIds = field.files;
        break;
      }
      case 'gpsLocation': {
        response.values = field.values;
        break;
      }
      case 'weather': {
        response.value = field.value;
        response.showDailySnapshot = field.showDailySnapshot;
        response.showWeatherReport = field.showWeatherReport;
        response.startHour = field.startHour;
        response.endHour = field.endHour;
        break;
      }
      case 'timeTrackingGrid': {
        response.values = field.values;
        response.columns = field.columns;
        response.date = field.date;
        response.dataType = field.dataType;
        response.timezone = field.timezone;
        break;
      }
      default:
        break;
    }

    const data = {
      title,
      type,
      response,
      fieldId,
      sectionId,
      duplicatedParentId,
    };

    return { data, field };
  }));

  parsedFields.forEach(({ data, field }) => {
    if (!data) return;
    const { sectionName, sectionDuplicatedParentId } = field;
    const key = data.sectionId || sectionName;
    if (!sectionMap[key]) {
      sectionMap[key] = {
        fields: [],
        name: sectionName,
        duplicatedParentId: sectionDuplicatedParentId,
      };
      if (addSectionId) sectionMap[key].id = data.sectionId;
    }
    sectionMap[key].fields.push(data);
  });

  const sections = Object.values(sectionMap);
  const payload = {
    templateId,
    data: {
      name,
      sections,
      collected,
    },
    files: fileList,
  };
  const ourId = id;
  if (ourId) payload.formId = ourId;
  if (isOnEditStep) payload.isOnEditStep = true;
  if (isResubmit) payload.isResubmit = true;
  if (isEdit) payload.isEdit = true;
  if (cardId) payload.cardId = cardId;
  if (valueFieldId) payload.valueFieldId = valueFieldId;
  return payload;
};

export const formsValuesAreDifferent = ({
  initialData,
  fileMap,
  sections,
  responses,
  defaultReturn = true,
}) => {
  // Check whether two forms have different responses
  const fieldMap = {};
  sections.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      const { id: fieldId } = field;
      fieldMap[fieldId] = field;
    });
  });

  let hasNewData = !initialData || !fileMap;
  if (!hasNewData) {
    const originalResponses = preparePreloadedFormTriggerData({
      initialData,
      fileMap,
      sections,
    });
    const { calculationFields } = FormHelpers.getCalculationFields({ sections, responses });
    // Only skip standalone calculation fields. Table calc columns reuse the table
    // field id with a colKey — excluding those ids hides Save Draft for the whole table.
    const calculationFieldsIds = calculationFields
      .filter((f) => !f.colKey)
      .map((f) => f.id);
    hasNewData = Object.keys(responses).some((fieldId) => {
      if (calculationFieldsIds.includes(fieldId)) return false;
      const {
        [fieldId]: field = {},
      } = fieldMap;
      const {
        [fieldId]: response = {},
      } = responses;
      const {
        [fieldId]: originalResponse = {},
      } = originalResponses;

      let ourType = field.selectedType;
      if (ourType === 'dynamicAttribute') {
        ourType = field.configProps?.fieldType ?? ourType;
      }

      if (
        !(fieldId in originalResponses)
        && ourType !== 'staticText'
        && ourType !== 'staticAttachments'
      ) return true;

      switch (ourType) {
        case 'staticText':
        case 'staticAttachments':
          return false;
        case 'attribute':
        case 'text': {
          const originalResponseValue = originalResponse.value;
          const responseValue = response.value;
          if (!originalResponseValue && !responseValue) return false;
          return originalResponseValue !== responseValue;
        }
        case 'yes-no': {
          return response.value !== originalResponse.value
            || response.explanation !== originalResponse.explanation;
        }
        case 'attachment': {
          if (response?.files?.length !== originalResponse?.files?.length) return true;
          return false;
        }
        case 'dropdown': {
          const key = 'values';
          const hasSame = (response[key] && originalResponse[key])
            || (!response[key] && !originalResponse[key]); // XOR
          if (!hasSame) return true;
          if (!response[key]) return false; // Both empty;
          return response[key].length !== originalResponse[key].length
            || response[key].some((val, index) => (
              getDropdownId(val) !== getDropdownId(originalResponse[key][index])
            ));
        }
        case 'table': {
          const {
            values: rVals = [],
            columns = [],
          } = response;
          const {
            values: oVals = [],
          } = originalResponse;

          const { configProps: { dataType } = {} } = field;

          return rVals.length !== oVals.length
            || rVals.some((val, index) => (
              columns.some((col) => {
                const dataKey = getTableColumnDataKey(col, dataType);
                return !oVals[index] || val[dataKey] !== oVals[index][dataKey];
              })
            ));
        }
        case 'dateRange': {
          return response.startTime !== originalResponse.startTime
          || response.endTime !== originalResponse.endTime
          || response.repeat !== originalResponse.repeat
          || response.repeatEndDate !== originalResponse.repeatEndDate;
        }
        case 'dateTime': {
          return response.date !== originalResponse.date
          || response.time !== originalResponse.time;
        }
        case 'gpsLocation': {
          const {
            values,
          } = response;
          const {
            values: oldValues,
          } = originalResponse;
          return (
            (values && !oldValues)
            || (!values && oldValues)
            || (values?.length !== oldValues?.length)
            || (
              values?.some((val, index) => {
                const oldVal = oldValues[index] ?? {};
                return (
                  val.id !== oldVal.id
                  || val.address !== oldVal.address
                  || val.lat !== oldVal.lat
                  || val.lng !== oldVal.lng
                );
              })
            )
          );
        }
        default: return defaultReturn;
      }
    });
  }
  return hasNewData;
};
export const floatIsBad = (text = '') => {
  if (!text) return true;
  const parsedFloat = parseFloat(text);
  if (!Number.isNaN(parsedFloat)) return false;
  const textString = text.toString();
  const len = textString.length;
  if (len === 1 && textString === '.') return true;
  if (textString.charAt(len - 1) === '.') return true;
  let decimalCount = 0;
  for (let i = 0; i < textString.length; i += 1) {
    const c = textString.charAt(i);
    if (c === '.') {
      decimalCount += 1;
      if (decimalCount > 1) return true;
    } else if (Number.isNaN(parseInt(c, 10))) {
      return true;
    }
  }
  return false;
};

export const validateTimeEntryTableResponses = ({ sections, responses }) => {
  let isValid = true;

  sections?.forEach(({ fields = [] } = {}) => {
    fields?.forEach(({
      id,
      selectedType,
      configProps: {
        dataType,
        enableWarningThreshold,
        warningThreshold,
      } = {},
      response,
    } = {}) => {
      const relevantResponse = response || responses[id];

      if (
        selectedType === 'table'
        && dataType === 'TimeEntry'
        && enableWarningThreshold
        && relevantResponse?.values?.length
        && isValid
      ) {
        const responseEntries = relevantResponse.values;
        isValid = responseEntries.some((entry) => isEntryTimeValid(entry, warningThreshold));
      }
    });
  });

  return isValid;
};
